<template>
    <div class="sidebar flex" :class="{ 'is-fixed': isSidebarFixed }" :style="containerStyle">
        <div ref="navContainer" class="nav-container" :style="navStyle">
            <span
                class="mobile-button"
                @click="handleMobileNav"
            >
                <i class="fal fa-bars" />
            </span>
            <svg-vue class="logo-small" icon="logo-small" />
            <base-sidebar-navigation />
        </div>
        <user-menu ref="userMenu" />
        <impersonation-banner />
    </div>
</template>

<script>
import ImpersonationBanner from './ImpersonationBanner.vue';
import UserMenu from './UserMenu.vue';
import Hammer from 'hammerjs';
import { updateBodyWidth } from '../../utils/bodyWidth';

const _overlay = document.getElementById('overlay');

export default {
    components: {
        ImpersonationBanner,
        UserMenu,
    },

    props: {
        hasNotification: {
            type: Boolean,
            default: true,
        },
    },

    data: () => ({
        mobileNavOpen: false,
        swipeHandler: null,
        isSidebarFixed: false,
        deltaHeight: 0,
        pageOffset: window.pageYOffset,
    }),

    computed: {
        /**
         * Returns styles object with top position for menu container.
         *
         * @returns {object|void} Style object.
         */
        navStyle() {
            if (this.isSidebarFixed) {
                const delta = 0 > this.deltaHeight
                    ? this.deltaHeight
                    : Math.max(0, this.calculateNotificationsHeights(this.hasNotification) - this.pageOffset);

                return { top: `${delta}px` };
            }

            return null;
        },

        /**
         * Returns styles object with top position for menu container.
         *
         * @returns {object|void} Style object.
         */
        containerStyle() {
            if (this.isSidebarFixed) {
                return 'sm' === this.$mq
                    ? {}
                    : { height: `calc(100% - ${this.calculateNotificationsHeights(this.hasNotification)}px)` };
            }

            return null;
        },

        /**
         * Returns notification height.
         *
         * @returns {number} Notification height.
         */
        upgradeNotificationHeight() {
            return this.calculateNotificationsHeights(this.hasNotification);
        },
    },

    watch: {
        /**
         * Watch route change
         */
        '$route'() {
            if (_overlay.classList.contains('active')) {
                this.mobileNavOpen = false;
                _overlay.classList.remove('active');
                this.$emit('open-mobile-nav', false);
            }
        },

        /**
         * Watch notification change
         *
         */
        hasNotification() {
            if (this.hasNotification) {
                updateBodyWidth();
            }
        },
    },

    /**
     * Created hook
     */
    created() {
        window.addEventListener('resize', () => {
            this.calculateNotificationsHeights(this.hasNotification);
        });
    },

    /**
     * Mounted hook
     *
     */
    mounted() {
        if ('sm' === this.$mq) {
            this.handleSwipe();
            this.overlayHandler();
        }

        this.$once('hook:destroyed', () => {
            if ('sm' === this.$mq) {
                this.swipeHandler.off();
            }
        });

        // Store the bound handlers so they can be detached in beforeDestroy.
        // Without removal, every remount leaks a resize/scroll listener that keeps
        // firing against a destroyed instance whose `$refs.userMenu` is undefined.
        this.boundHandleResize = this.handleResize.bind(this);
        this.boundSetMenuPosition = this.setMenuPosition.bind(this);

        window.addEventListener('resize', this.boundHandleResize);
        window.addEventListener('scroll', this.boundSetMenuPosition);
        this.handleResize();
    },

    /**
     * Before destroy hook
     */
    beforeDestroy() {
        window.removeEventListener('resize', this.boundHandleResize);
        window.removeEventListener('scroll', this.boundSetMenuPosition);
    },

    methods: {
        /**
         * Calculate notifications heights
         *
         * @param {boolean} hasNotification If is displayed notification.
         *
         * @returns {number} Notifications heights.
         */
        calculateNotificationsHeights(hasNotification) {
            if (hasNotification) {
                const notification = document.querySelector('.dashboard > .notification');
                if (notification) {
                    return notification.clientHeight;
                }
            }
        },

        /**
         * Handles sidebar sizing and positioning on window resize
         *
         * @returns {void}
         */
        handleResize() {
            const userMenuEl = this.$refs.userMenu?.$el;

            if (! userMenuEl || 'fixed' !== window.getComputedStyle(userMenuEl).position) {
                return;
            }

            this.deltaHeight = window.innerHeight - this.$refs.navContainer.clientHeight;
            this.setMenuPosition();
        },

        /**
         * Handles sidebar sizing and positioning on window resize
         *
         * @returns {void}
         */
        setMenuPosition() {
            const delta = this.deltaHeight;

            this.pageOffset = window.pageYOffset;

            this.isSidebarFixed = 0 <= delta ||
                window.pageYOffset > -delta;
        },

        /**
         * Handles mobile nav
         *
         */
        handleMobileNav() {
            this.mobileNavOpen = ! this.mobileNavOpen;
            _overlay.classList.toggle('active');
            this.$emit('open-mobile-nav', this.mobileNavOpen);
        },

        /**
         * Handles overlay click
         *
         */
        overlayHandler() {
            _overlay.addEventListener('click', () => {
                this.handleMobileNav();
            });
        },

        /**
         * Handles swipe gestures on body
         *
         */
        handleSwipe() {
            this.swipeHandler = new Hammer(document.body);

            this.swipeHandler.on('swiperight', () => {
                if (! this.mobileNavOpen) {
                    this.handleMobileNav();
                }
            });

            this.swipeHandler.on('swipeleft', () => {
                if (this.mobileNavOpen) {
                    this.handleMobileNav();
                }
            });
        },
    },
};
</script>
